import React, { useEffect, useRef } from 'react';
import './WavyBackground.css';

const WavyBackground = ({
  children,
  className = '',
  containerClassName = '',
  backgroundFill = '#0f0f23',
  colors = ['#ffd700', '#ffed4e', '#ffa500', '#ffd700'],
  waveWidth = 50,
  blur = 10,
  speed = 'fast',
  waveOpacity = 0.5,
}) => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const timeRef = useRef(0);

  // Fonction helper pour convertir hex en rgba
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationId;

    const resizeCanvas = () => {
      const container = canvas.parentElement;
      if (container) {
        canvas.width = container.offsetWidth || window.innerWidth;
        canvas.height = container.offsetHeight || window.innerHeight;
      } else {
        canvas.width = canvas.offsetWidth || window.innerWidth;
        canvas.height = canvas.offsetHeight || window.innerHeight;
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Observer pour détecter les changements de taille du conteneur
    const resizeObserver = new ResizeObserver(() => {
      resizeCanvas();
    });
    
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }

    const speedMap = {
      slow: 0.5,
      fast: 2,
      normal: 1,
    };

    const speedValue = speedMap[speed] || 1;

    const drawWave = (y, color, amplitude, frequency, phase) => {
      ctx.beginPath();
      ctx.moveTo(0, y);

      for (let x = 0; x <= canvas.width; x += 1) {
        const waveY =
          y +
          amplitude *
            Math.sin(
              (x / canvas.width) * Math.PI * 2 * frequency +
                timeRef.current * speedValue * 0.01 +
                phase
            );
        ctx.lineTo(x, waveY);
      }

      ctx.lineTo(canvas.width, canvas.height);
      ctx.lineTo(0, canvas.height);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
    };

    const animate = () => {
      ctx.fillStyle = backgroundFill;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const waveCount = colors.length;
      const sectionHeight = canvas.height / waveCount;

      colors.forEach((color, index) => {
        const y = (index + 1) * sectionHeight;
        const amplitude = waveWidth;
        const frequency = 1 + index * 0.5;
        const phase = (index * Math.PI) / waveCount;

        const rgbaColor = hexToRgba(color, waveOpacity);
        drawWave(y, rgbaColor, amplitude, frequency, phase);
      });

      timeRef.current += 1;
      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [colors, waveWidth, blur, speed, waveOpacity, backgroundFill]);

  return (
    <div 
      className={`wavy-background-container ${containerClassName}`} 
      style={{ 
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
        overflow: 'hidden'
      }}
    >
      <canvas
        ref={canvasRef}
        className="wavy-background-canvas"
        style={{
          filter: `blur(${blur}px)`,
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          pointerEvents: 'none',
          transition: 'opacity 0.3s ease, filter 0.3s ease',
        }}
      />
    </div>
  );
};

export default WavyBackground;

